import { ReactElement, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';

import Button from '~/ui/components/common/Button';
import Modal from '~/ui/components/common/Modal';
import DatePicker from '~/ui/components/inputs/DatePicker';
import SelectComponent from '~/ui/components/inputs/SelectWithoutAnimation';
import { extractErrorMessage } from '~/utils/error/error';
import validate from './validate';

import useClientMultiTeam from '~/hooks/useClientMultiTeamId';
import { IClient } from '~/services/api/client/types';
import { IDischargeDetails } from '~/store/client/types';
import { useStoreActions } from '~/store/hooks';
import { IClientAction } from '../../../reusable/Table/types';

import styles from '../ClientsManagement.module.scss';

interface IProps {
  client: IClient;
  setClientAction: (payload: IClientAction | null) => void;
  options: { label: string; value: number }[];
  clinicId: number;
  refetch?: () => void;
}

const ModalForm = ({
  setClientAction,
  client,
  options,
  clinicId,
  refetch,
}: IProps): ReactElement => {
  const clientTeams = useMemo(
    () => client.teams.filter(t => !t.isArchived && !t.hasUnresolvedCrisisCall),
    [client.teams],
  );
  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      teamId: clientTeams.length === 1 ? clientTeams[0]?.team?.id : null,
      dischargeReasonId: null,
      dischargeDate: null,
    },
    resolver: validate,
  });

  const { getClientMultiTeamId } = useClientMultiTeam();
  const clientTeamId = getClientMultiTeamId(client?.teams);

  const onArchiveClient = useStoreActions(actions => actions.client.onArchiveClient);
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const onSubmit = async (vals: IDischargeDetails) => {
    const selectedTeamId = String(vals.teamId || clientTeamId);
    try {
      await onArchiveClient({
        clinicId: String(clinicId),
        clientId: String(client.id),
        teamId: String(vals.teamId || clientTeamId),
        id: String(client?.teams.find(t => t.team.id === Number(selectedTeamId))?.id || ''),
        ...vals,
      });
      showNotify({ message: 'Client successfully archived' });
      setClientAction(null);
      refetch?.();
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const teamOptions = useMemo(
    () => clientTeams.map(t => ({ label: t.team.name, value: t.team.id, isPrimary: t.isPrimary })),
    [clientTeams],
  );

  const primaryTeam = useMemo(() => teamOptions.find(t => t.isPrimary), [teamOptions]);
  const isSelectedPrimaryTeam = primaryTeam?.value === watch('teamId');

  return (
    <Modal open onClose={() => setClientAction(null)} maxWidth="lg" className={styles.modal}>
      <>
        <p className={styles.modalText}>
          Are you sure you want to archive {client.firstName} {client.lastName}?
        </p>
        <form onSubmit={handleSubmit(onSubmit as SubmitHandler<FieldValues>)}>
          <div className={styles.input}>
            <SelectComponent
              name="teamId"
              hideSelectedOptions={false}
              options={teamOptions}
              control={control}
              errors={errors}
              maxMenuHeight={200}
              label="Select Team"
            />
            {isSelectedPrimaryTeam && teamOptions.length > 1 && (
              <p className={styles.notePrimaryTeam}>Note: You cannot archive a primary team</p>
            )}
          </div>
          <div className={styles.input}>
            <SelectComponent
              isRelativeWindow
              maxMenuHeight={350}
              options={options}
              name="dischargeReasonId"
              control={control}
              label="Reason for Discharge"
              errors={errors}
              hideSelectedOptions={false}
            />
          </div>
          <DatePicker
            name="dischargeDate"
            openTo="date"
            control={control}
            errors={errors}
            label="Date of Discharge"
          />
          <div className={styles.buttonsWrapper}>
            <div className={styles.buttonMargin}>
              <Button color="primary" variant="outlined" onClick={() => setClientAction(null)}>
                Cancel
              </Button>
            </div>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={!isValid || (isSelectedPrimaryTeam && teamOptions.length > 1)}
            >
              Archive
            </Button>
          </div>
        </form>
      </>
    </Modal>
  );
};

export default ModalForm;
